<template>
	<div class="abiturient-edit-request-view">
		<v-row>
			<v-breadcrumbs :items="breadcrumbs" divider="/"></v-breadcrumbs>
		</v-row>

		<v-fade-transition>
			<div v-if="isLoading" class="requests-loader" aria-busy="true" aria-live="polite">
				<v-skeleton-loader type="article, actions" class="mb-4"></v-skeleton-loader>
				<v-progress-linear indeterminate color="primary" rounded height="3"></v-progress-linear>
			</div>
		</v-fade-transition>

		<v-fade-transition>
			<v-card v-if="!isLoading" elevation="2">
				<v-card-title>{{ $t('Редактирование заявки') }}</v-card-title>
				<v-card-text>
					<v-form class="form" ref="editForm">
						<v-alert dense outlined type="info" class="mb-4">
							<strong>{{ $t('Тип заявителя') }}:</strong> {{ declarantTypeLabel }}
						</v-alert>

						<v-card outlined class="mb-4">
							<v-card-title>{{ $t('Данные абитуриента') }}</v-card-title>
							<v-card-text>
								<v-row>
									<v-col sm="3" cols="12">
										<v-text-field
											v-model="requestData.surname"
											:label="$t('Фамилия')"
											type="text"
										></v-text-field>
									</v-col>
									<v-col sm="3" cols="12">
										<v-text-field
											v-model="requestData.name"
											:label="$t('Имя')"
											type="text"
											:rules="[v => !!v || $t('Обязательное поле')]"
											required
										></v-text-field>
									</v-col>
									<v-col sm="3" cols="12">
										<v-text-field
											v-model="requestData.lastname"
											:label="$t('Отчество')"
											type="text"
										></v-text-field>
									</v-col>
									<v-col sm="3" cols="12">
										<v-menu
											v-model="datePickerMenu"
											:close-on-content-click="false"
											transition="scale-transition"
											offset-y
											min-width="auto"
										>
											<template v-slot:activator="{ on, attrs }">
												<v-text-field
													v-model="requestData.birthday"
													:label="$t('Дата рождения')"
													readonly
													prepend-icon="mdi-calendar"
													:rules="birthdayDateRules"
													required
													v-bind="attrs"
													v-on="on"
												></v-text-field>
											</template>
											<v-date-picker
												v-model="requestData.birthday"
												min="1901-01-01"
												:max="maxBirthdayDate"
												@input="datePickerMenu = false"
											></v-date-picker>
										</v-menu>
									</v-col>
								</v-row>
								<v-row>
									<v-col sm="4" cols="12">
										<v-select
											v-model="requestData.citizen_status_id"
											:items="citizenStatusList"
											item-text="name"
											item-value="id"
											:rules="[v => !!v || $t('Обязательное поле')]"
											:label="$t('Статус гражданина')"
										></v-select>
									</v-col>
									<v-col sm="4" cols="12">
										<v-select
											v-model="requestData.nationality_id"
											:items="nationalityList"
											item-text="name"
											item-value="id"
											:rules="[v => !!v || $t('Обязательное поле')]"
											:label="$t('Национальность')"
										></v-select>
									</v-col>
									<v-col sm="4" cols="12">
										<v-text-field
											v-model="requestData.iin"
											:label="$t('ИИН')"
											type="number"
											:rules="[v => !v || v.match(/\d/g).length === 12 || $t('Неправильный формат ИИН')]"
											@wheel="$event.target.blur()"
										></v-text-field>
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>

						<v-card outlined class="mb-4">
							<v-card-title>{{ $t('Контактные данные') }}</v-card-title>
							<v-card-text>
								<v-row>
									<v-col sm="4" cols="12">
										<v-text-field
											v-model="requestData.email"
											:label="$t('Электронная почта')"
											type="text"
											:rules="[v => !v || /.+@.+\..+/.test(v) || $t('Введите корректный E-mail')]"
										></v-text-field>
									</v-col>
									<v-col sm="4" cols="12">
										<v-text-field
											v-model="requestData.phone_number"
											:label="$t('Номер телефона абитуриента')"
											prefix="+"
											type="number"
											@wheel="$event.target.blur()"
											:rules="[v => !!v || $t('Обязательное поле'), v => !v || v.match(/\d/g).length === 11 || $t('Неправильный формат телефона')]"
											required
										></v-text-field>
									</v-col>
									<v-col sm="4" cols="12">
										<v-text-field
											v-model="requestData.parent_phone_number"
											:label="$t('Номер телефона родителя')"
											type="number"
											prefix="+"
											@wheel="$event.target.blur()"
											:rules="[v => !!v || $t('Обязательное поле'), v => !v || v.match(/\d/g).length === 11 || $t('Неправильный формат телефона')]"
											required
										></v-text-field>
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>

						<v-card outlined class="mb-4">
							<v-card-title>{{ $t('Медицинские данные') }}</v-card-title>
							<v-card-text>
								<v-file-input
									:label="$t('Медицинская справка 075/У (086)')"
									accept="image/*,application/pdf"
									:rules="[v => !v || v.size < 10485760 || $t('Размер файла не должен превышать 10 Мб')]"
									@change="onForm086Change"
								></v-file-input>
							</v-card-text>
						</v-card>

						<v-card outlined class="mb-4">
							<v-card-title>{{ $t('Данные об образовании') }}</v-card-title>
							<v-card-text>
								<v-row
									v-for="(field, index) in educationDisplayFields"
									:key="'education-' + index"
									class="mb-2"
									no-gutters
								>
									<v-col cols="12" sm="5" class="text--secondary">{{ field.label }}</v-col>
									<v-col cols="12" sm="7">{{ field.value }}</v-col>
								</v-row>
								<v-file-input
									class="mt-4"
									:label="$t('Документ об образовании')"
									accept="image/*,application/pdf"
									:rules="[v => !v || v.size < 10485760 || $t('Размер файла не должен превышать 10 Мб')]"
									@change="onEducationDocumentChange"
								></v-file-input>
							</v-card-text>
						</v-card>

						<v-card outlined class="mb-4">
							<v-card-title>{{ $t('Квалификации') }}</v-card-title>
							<v-card-text>
								<v-row class="mb-4" no-gutters>
									<v-col cols="12" sm="5" class="text--secondary">{{ $t('Форма обучения') }}</v-col>
									<v-col cols="12" sm="7">{{ qualificationContext.education_form }}</v-col>
								</v-row>

								<v-card
									outlined
									class="mb-4"
									v-for="(item, index) in requestData.choosed_qualification"
									:key="'qualification-' + index"
								>
									<v-card-text>
										<v-btn
											fab
											small
											depressed
											text
											v-if="index != 0"
											@click.stop="removeQualificationBlock(index)"
											style="position: absolute; top: 0; right: 0;"
										>
											<v-icon>mdi-close</v-icon>
										</v-btn>
										<v-autocomplete
											v-model="qualificationSelected[index]"
											:items="qualificationList"
											item-value="qualification_code"
											:label="$t('Специальность №') + (index + 1)"
											@change="qualificationChanged($event, index)"
											:filter="customQualificationFilter"
											:rules="[v => !v || Object.keys(v).length != 0 || $t('Обязательное поле')]"
											return-object
										>
											<template v-slot:item="{ item }">
												<div class="py-3">{{ item.display_label }}</div>
											</template>
											<template v-slot:selection="{ item }">
												<span style="text-overflow: ellipsis; overflow: hidden; width: 90%; white-space: nowrap; position: absolute;">
													{{ item.display_label }}
												</span>
											</template>
										</v-autocomplete>
										<v-select
											v-if="qualificationSelected[index] && qualificationSelected[index].colleges"
											v-model="item.college_id"
											:items="collegeList[index]"
											@change="newApiCollegeChanged(index)"
											item-text="name"
											item-value="college_id"
											:rules="[v => !!v || $t('Обязательное поле')]"
											:label="$t('Организация образования')"
										></v-select>
										<v-select
											v-if="item.college_id"
											v-model="item.lang"
											:items="collegeLangList[index]"
											item-text="name"
											item-value="lang_short"
											:rules="[v => !!v || $t('Обязательное поле')]"
											:label="$t('Язык обучения')"
										></v-select>
									</v-card-text>
								</v-card>
								<v-btn
									color="primary"
									@click.stop="addQualificationBlock"
									v-if="canAddQualification"
								>
									{{ $t('Добавить еще одну специальность') }}
								</v-btn>
							</v-card-text>
						</v-card>

						<v-select
							v-model="requestData.quota_list[0].id"
							:items="quotaCategoryList"
							item-text="name"
							item-value="id"
							:rules="[v => v !== null && v !== undefined && v !== '' || $t('Обязательное поле')]"
							:label="$t('Тип льготы')"
						></v-select>

						<v-file-input
							v-if="needsQuotaFile"
							:label="$t('Документ подтверждающий льготу')"
							accept="image/*,application/pdf"
							:rules="[quotaDocumentFileRule, v => !v || v.size < 10485760 || $t('Размер файла не должен превышать 10 Мб')]"
							@change="onQuotaDocumentChange"
						></v-file-input>
					</v-form>
				</v-card-text>
				<v-card-actions class="px-4 pb-4">
					<v-btn text :to="`/abiturient/my-requests/${orderId}`">{{ $t('Отмена') }}</v-btn>
					<v-spacer></v-spacer>
					<v-btn color="primary" :loading="isSaving" @click="save">
						{{ $t('Сохранить') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-fade-transition>
	</div>
</template>

<script>
	import {
		mapMetadataToSelectItems,
		getMetadataLabel,
		parseQualificationCodeKey,
		mapQualificationApiResponse,
		buildQualificationFormBody,
		mapCollegeLangOptions,
		mapInfoOrderToEditData,
		buildEducationDisplayFields,
		buildQualificationItemFromOrder,
		resolveCollegeIdFromOrder,
		resolveStudyLangShort,
		normalizeRegions,
		normalizeRegionListResponse,
		canEditOrder,
	} from '@/utils/metadata';
	import { buildEditOrderFormData, createBirthdayDateRules, formatMaxBirthdayDate } from '@/utils/orderForm';

	export default {
		mounted() {
			if (!this.$store.getters.usesAbiturientNewApi) {
				this.$router.replace(`/abiturient/my-requests/${this.$route.params.id}`);
				return;
			}
			this.loadData();
		},
		computed: {
			breadcrumbs() {
				return [
					{
						text: this.$t('Главная'),
						disabled: false,
						exact: true,
						to: '/abiturient',
					},
					{
						text: this.$t('Мои заявки'),
						disabled: false,
						exact: true,
						to: '/abiturient/my-requests',
					},
					{
						text: this.$t('Просмотр заявки'),
						disabled: false,
						exact: true,
						to: `/abiturient/my-requests/${this.orderId}`,
					},
					{
						text: this.$t('Редактирование заявки'),
						disabled: true,
						exact: true,
						to: '',
					},
				];
			},
			declarantTypeLabel() {
				return getMetadataLabel(this.metadata && this.metadata.declarant_type, this.requestData.declarant_type_id)
					|| this.$t('Поступающий/Учащийся');
			},
			needsQuotaFile() {
				const quotaId = this.requestData.quota_list[0].id;
				return quotaId && quotaId !== 0 && quotaId !== '0' && quotaId !== 3 && quotaId !== '3';
			},
			birthdayDateRules() {
				return createBirthdayDateRules((key) => this.$t(key));
			},
			maxBirthdayDate() {
				return formatMaxBirthdayDate();
			},
			canAddQualification() {
				return this.requestData.payment_type_id == 2
					&& this.requestData.training_type_id == 1
					&& this.maxChoosedQualificationCount > this.requestData.choosed_qualification.length;
			},
		},
		data: () => ({
			isLoading: true,
			isSaving: false,
			orderId: null,
			datePickerMenu: false,
			metadata: null,
			originalData: null,
			educationDisplayFields: [],
			qualificationContext: {
				education_form: '—',
			},
			maxChoosedQualificationCount: 4,
			qualificationList: [],
			qualificationSelected: [],
			collegeList: [],
			collegeLangList: [],
			choosedQualificationItem: {
				specialty_code: null,
				code: null,
				qualification_id: null,
				bin: null,
				lang: null,
				college_id: null,
			},
			requestData: {
				declarant_type_id: null,
				citizen_status_id: null,
				nationality_id: null,
				iin: null,
				surname: '',
				name: '',
				lastname: '',
				birthday: null,
				email: '',
				phone_number: null,
				parent_phone_number: null,
				graduation_year: null,
				education_type_id: null,
				payment_type_id: null,
				training_type_id: null,
				education_form_id: null,
				region_ab: null,
				school_country_id: 398,
				school_region_ab: null,
				school_id: null,
				education_document_type_id: null,
				quota_list: [{ id: null }],
				choosed_qualification: [],
			},
			citizenStatusList: [],
			nationalityList: [],
			quotaCategoryList: [],
			allRegionList: [],
			form086File: null,
			educationDocumentFile: null,
			quotaDocumentFile: null,
		}),
		watch: {
			'$store.state.language': function () {
				this.loadData();
			},
		},
		methods: {
			onForm086Change(file) {
				this.form086File = file || null;
			},
			onEducationDocumentChange(file) {
				this.educationDocumentFile = file || null;
			},
			onQuotaDocumentChange(file) {
				this.quotaDocumentFile = file || null;
			},
			quotaDocumentFileRule(v) {
				if (!this.needsQuotaFile) {
					return true;
				}
				const originalQuota = this.originalData
					&& this.originalData.quota_list
					&& this.originalData.quota_list[0]
					&& this.originalData.quota_list[0].id;
				const quotaChanged = this.requestData.quota_list[0].id !== originalQuota;
				if (quotaChanged && !v && !this.quotaDocumentFile) {
					return this.$t('Обязательное поле');
				}
				return true;
			},
			customQualificationFilter(item, queryText) {
				const searchText = queryText.toLowerCase();
				const displayLabel = (item.display_label || '').toLowerCase();
				const name = (item.name || '').toLowerCase();
				const qualificationCode = (item.qualification_code || '').toLowerCase();
				const specialtyCode = (item.specialty_code || '').toLowerCase();
				return displayLabel.includes(searchText)
					|| name.includes(searchText)
					|| qualificationCode.includes(searchText)
					|| specialtyCode.includes(searchText);
			},
			qualificationCodesMatch(apiCode, orderCode) {
				const apiParsed = parseQualificationCodeKey(apiCode || '');
				const orderParsed = parseQualificationCodeKey(orderCode || '');
				return apiParsed.qualification_code === orderParsed.qualification_code
					&& (!orderParsed.specialty_code || apiParsed.specialty_code === orderParsed.specialty_code);
			},
			addQualificationBlock() {
				this.qualificationSelected.push({});
				this.requestData.choosed_qualification.push({ ...this.choosedQualificationItem });
				this.collegeList.push([]);
				this.collegeLangList.push([]);
			},
			removeQualificationBlock(index) {
				this.requestData.choosed_qualification.splice(index, 1);
				this.qualificationSelected.splice(index, 1);
				this.collegeList.splice(index, 1);
				this.collegeLangList.splice(index, 1);
			},
			qualificationChanged(item, index) {
				this.requestData.choosed_qualification[index].bin = null;
				this.requestData.choosed_qualification[index].college_id = null;
				this.requestData.choosed_qualification[index].lang = null;
				this.requestData.choosed_qualification[index].qualification_id = null;
				this.collegeList[index] = [];
				this.collegeLangList[index] = [];
				if (!item) {
					return;
				}
				this.requestData.choosed_qualification[index].specialty_code = item.specialty_code || '';
				this.requestData.choosed_qualification[index].code = item.qualification_code;
				this.requestData.choosed_qualification[index].qualification_id = item.qualification_id;
				this.collegeList[index] = item.colleges || [];
				if (item.colleges && item.colleges.length === 1) {
					this.requestData.choosed_qualification[index].college_id = item.colleges[0].college_id;
					this.newApiCollegeChanged(index);
				}
			},
			newApiCollegeChanged(index) {
				const chosen = this.requestData.choosed_qualification[index];
				chosen.lang = null;
				chosen.bin = null;
				if (!chosen.college_id) {
					this.collegeLangList[index] = [];
					return;
				}
				this.syncCollegeLangSelection(index);
			},
			async getQualification() {
				if (!this.requestData.region_ab || !this.requestData.education_form_id) {
					this.qualificationList = [];
					return;
				}
				const body = buildQualificationFormBody(this.requestData);
				const response = await this.$http.post('/qualification', body, {
					headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
				});
				const data = response.data.data || response.data;
				this.qualificationList = mapQualificationApiResponse(data);
			},
			async resolveRegionForQualifications() {
				if (this.requestData.region_ab || !this.requestData.choosed_qualification.length) {
					return;
				}
				const candidateRegions = normalizeRegions(this.allRegionList);
				for (const region of candidateRegions) {
					this.requestData.region_ab = region.id;
					await this.getQualification();
					const allMatched = this.requestData.choosed_qualification.every((chosen) => (
						this.qualificationList.some((item) => this.qualificationCodesMatch(item.qualification_code, chosen.code))
					));
					if (allMatched) {
						return;
					}
				}
				this.requestData.region_ab = null;
				this.qualificationList = [];
			},
			ensureQualificationListIncludesOrderItems() {
				this.requestData.choosed_qualification.forEach((chosen) => {
					const exists = this.qualificationList.some((item) => (
						this.qualificationCodesMatch(item.qualification_code, chosen.code)
					));
					if (!exists) {
						this.qualificationList.push(buildQualificationItemFromOrder(chosen, this.metadata));
					}
				});
			},
			syncCollegeLangSelection(index) {
				const chosen = this.requestData.choosed_qualification[index];
				const selected = this.qualificationSelected[index];
				const collegeId = chosen.college_id;

				if (!collegeId || !selected) {
					this.collegeLangList[index] = [];
					return;
				}

				const college = (selected.colleges || []).find((entry) => entry.college_id === collegeId);
				if (college) {
					this.collegeLangList[index] = mapCollegeLangOptions(college.lang, this.metadata);
					const langShort = resolveStudyLangShort(chosen.lang, this.metadata);
					if (langShort && this.collegeLangList[index].some((lang) => lang.lang_short === langShort)) {
						chosen.lang = langShort;
					} else if (college.lang && college.lang.length === 1) {
						chosen.lang = college.lang[0];
					}
					chosen.bin = college.college_id;
					return;
				}

				const langShort = resolveStudyLangShort(chosen.lang, this.metadata);
				this.collegeLangList[index] = langShort
					? mapCollegeLangOptions([langShort], this.metadata)
					: [];
				if (langShort) {
					chosen.lang = langShort;
				}
			},
			initQualificationSelections() {
				this.ensureQualificationListIncludesOrderItems();
				this.collegeList = [];
				this.collegeLangList = [];

				this.qualificationSelected = this.requestData.choosed_qualification.map((chosen) => (
					this.qualificationList.find((item) => this.qualificationCodesMatch(item.qualification_code, chosen.code))
					|| buildQualificationItemFromOrder(chosen, this.metadata)
				));

				this.qualificationSelected.forEach((selected, index) => {
					const chosen = this.requestData.choosed_qualification[index];
					if (selected.qualification_id) {
						chosen.qualification_id = selected.qualification_id;
					}
					this.collegeList[index] = selected.colleges || [];
					chosen.college_id = resolveCollegeIdFromOrder(selected.colleges, chosen);
					this.syncCollegeLangSelection(index);
				});
			},
			async loadQualificationsForEdit() {
				await this.resolveRegionForQualifications();
				if (this.requestData.region_ab) {
					await this.getQualification();
				}
				this.initQualificationSelections();
			},
			resolveRegionId(regionName, regionList) {
				if (!regionName || !regionList.length) {
					return null;
				}
				const normalizedName = String(regionName).trim();
				const match = regionList.find((item) => String(item.name).trim() === normalizedName);
				return match ? match.id : null;
			},
			async loadData() {
				this.isLoading = true;
				this.orderId = this.$route.params.id;
				try {
					const [
						metadataResponse,
						nationalityResponse,
						regionResponse,
						quotaResponse,
						orderResponse,
					] = await Promise.all([
						this.$http.get('/metadata'),
						this.$http.get('/nationality'),
						this.$http.get('/regions'),
						this.$http.get('/quota'),
						this.$http.get('/info-order', { params: { id: this.orderId } }),
					]);

					const regionList = normalizeRegionListResponse(regionResponse);
					this.allRegionList = regionList;
					this.metadata = metadataResponse.data.data || metadataResponse.data;
					this.citizenStatusList = mapMetadataToSelectItems(this.metadata.citizen_status);
					this.nationalityList = nationalityResponse.data.data || nationalityResponse.data || [];
					this.quotaCategoryList = quotaResponse.data.data || quotaResponse.data || [];
					this.quotaCategoryList.unshift({
						id: '0',
						name: this.$t('Без льготы'),
					});

					const order = orderResponse.data.data || {};
					if (!canEditOrder(order)) {
						this.$store.dispatch('alertAdd', {
							text: this.$t('Редактирование заявки недоступно'),
							type: 'error',
						});
						this.$router.replace(`/abiturient/my-requests/${this.orderId}`);
						return;
					}

					const mapped = mapInfoOrderToEditData(order, {
						metadata: this.metadata,
						nationalityList: this.nationalityList,
						regionList,
						quotaCategoryList: this.quotaCategoryList,
					});

					if (!mapped.school_region_ab) {
						const schoolInfo = order.school && typeof order.school === 'object'
							? order.school.region
							: order.school_region;
						mapped.school_region_ab = this.resolveRegionId(schoolInfo, regionList);
					}
					if (!mapped.region_ab) {
						mapped.region_ab = this.resolveRegionId(order.region, regionList);
					}

					this.educationDisplayFields = buildEducationDisplayFields(
						order,
						this.metadata,
						this.$t.bind(this),
						regionList
					);
					this.qualificationContext = {
						education_form: getMetadataLabel(this.metadata.education_form, mapped.education_form_id)
							|| order.education_form
							|| '—',
					};

					this.requestData = mapped;
					if (!this.requestData.choosed_qualification.length) {
						this.addQualificationBlock();
					} else {
						await this.loadQualificationsForEdit();
					}

					this.originalData = JSON.parse(JSON.stringify(this.requestData));
				} catch (error) {
					console.error('Ошибка загрузки заявки для редактирования:', error);
					this.$router.replace('/abiturient/my-requests');
				} finally {
					this.isLoading = false;
				}
			},
			async save() {
				if (!this.$refs.editForm.validate()) {
					return;
				}

				const files = {
					form086File: this.form086File,
					educationDocumentFile: this.educationDocumentFile,
					quotaDocumentFile: this.quotaDocumentFile,
				};
				const { formData, hasChanges } = buildEditOrderFormData(
					this.orderId,
					this.originalData,
					this.requestData,
					files
				);

				if (!hasChanges) {
					this.$store.dispatch('alertAdd', {
						text: this.$t('Нет изменений для сохранения'),
						type: 'info',
					});
					return;
				}

				this.isSaving = true;
				try {
					const response = await this.$http.post('/edit-order', formData);
					const message = (response.data && response.data.message)
						|| this.$t('Заявка успешно обновлена');
					this.$store.dispatch('alertAdd', { text: message, type: 'success' });
					this.$router.push(`/abiturient/my-requests/${this.orderId}`);
				} catch (error) {
					console.error('Ошибка сохранения заявки:', error);
				} finally {
					this.isSaving = false;
				}
			},
		},
	};
</script>
